import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { AuthContext } from "../../context/AuthContext";
import { collection, addDoc, getDocs, where, query, doc, getDoc, updateDoc, orderBy, limit } from "firebase/firestore";
import { fetchClientesPorAdministrativo, fetchClientesPorInspector } from '../../components/admin/firestoreService';
import { TextField, Select, MenuItem, Button, Typography, InputLabel, FormControl, Checkbox, FormControlLabel, ListItemText, Chip, Input } from '@mui/material';
import './styles/formCreator.scss';
import FormPreview from './FormPreview';
import { v4 as uuidv4 } from 'uuid';
import IconPicker from './formUtils/IconPicker';
import crudProfiler from '../../pages/utils/CRUDPerformanceProfiler';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

// Componentes modulares
import { SortableInput, InputTypeMenu, useFormHandlers } from './formComponents';
import { getPdfDesigns } from './formHelpers/pdfDesignHelper';

const FormUpdate = () => {
  const { currentUser, adminData } = useContext(AuthContext);
  const [form, setForm] = useState({
    icon: '',
    export: '',
    name: '',
    inputs: {},
    pdfDesignId: ''
  });
  const [servicio, setServicio] = useState(null);
  const [pdfDesigns, setPdfDesigns] = useState([]);
  const [errors, setErrors] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [selectedInput, setSelectedInput] = useState(null);
  const [selectedInputType, setSelectedInputType] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState('all');
  const [clients, setClients] = useState([]);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const [selectedClientNames, setSelectedClientNames] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [isAllClientsSelected, setIsAllClientsSelected] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);

  const CLIENTS_INITIAL_LIMIT = 50;
  const CLIENTS_SEARCH_LIMIT = 100;
  const CLIENTES_SEARCH_DEBOUNCE_MS = 300;
  const lastClientSearchTermRef = useRef('');

  // Cache de todos los clientes asignados (para búsqueda sobre la lista completa, como en New.jsx)
  const allClientesCacheRef = useRef([]);

  // Hook personalizado con todos los handlers de inputs
  const handlers = useFormHandlers(form, setForm);

  // Configuración de sensores para drag and drop
  // Solo ratón; sin KeyboardSensor para que espacio en inputs no active arrastre
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const navigate = useNavigate();
  const { id: existingFormId } = useParams();

  // Fetch servicio
  const fetchServicio = async () => {
    try {
      const serviciosRef = collection(db, 'servicios');
      const q = query(serviciosRef, where('admins', 'array-contains', currentUser.email));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const primerServicio = querySnapshot.docs[0];
        setServicio({ id: primerServicio.id, ...primerServicio.data() });
      }
    } catch (error) {
      console.error('Error al obtener el servicio:', error);
    }
  };

  // Cargar clientes como en New.jsx: cache completo (asignados o por rol), mostrar primeros 50, búsqueda sobre todo el cache
  // Soporta adminData.clientes y adminData.clientesAsignados (como Profile.js y NotificationCreator)
  const loadClientes = async () => {
    if (!servicio?.id) return;
    try {
      const clientesRef = collection(db, 'servicios', servicio.id, 'clientes');
      const assignedIds = (adminData?.clientes || adminData?.clientesAsignados || []).filter(Boolean);
      let loadedClientes = [];

      if (Array.isArray(assignedIds) && assignedIds.length > 0) {
        const BATCH_SIZE = 30; // Límite 'in' de Firestore
        for (let i = 0; i < assignedIds.length; i += BATCH_SIZE) {
          const batch = assignedIds.slice(i, i + BATCH_SIZE);
          const q = query(clientesRef, where('__name__', 'in', batch));
          const snapshot = await getDocs(q);
          const batchClientes = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
          loadedClientes = [...loadedClientes, ...batchClientes];
        }
      } else if (adminData?.id && adminData?.tipoUsuario) {
        if (adminData.tipoUsuario.includes('administrativo')) {
          loadedClientes = await fetchClientesPorAdministrativo(adminData.id, servicio) || [];
        } else if (adminData.tipoUsuario.includes('inspector')) {
          loadedClientes = await fetchClientesPorInspector(adminData.id, servicio) || [];
        }
      }

      loadedClientes.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
      allClientesCacheRef.current = loadedClientes;
      setClients(loadedClientes.slice(0, CLIENTS_INITIAL_LIMIT));
      setClientSearchResults([]);
    } catch (error) {
      console.error('Error al obtener los clientes:', error);
      allClientesCacheRef.current = [];
      setClients([]);
    }
  };

  useEffect(() => {
    if (servicio?.id && adminData != null) {
      loadClientes();
    }
  }, [servicio?.id, adminData?.clientes?.length, adminData?.clientesAsignados?.length, adminData?.tipoUsuario]);

  // Búsqueda sobre todo el cache (como New.jsx): debounce 300ms, mínimo 2 caracteres, ordenar por empieza con término
  useEffect(() => {
    const term = clientSearchTerm.trim().toLowerCase();
    if (term.length >= 2) {
      const t = setTimeout(() => {
        setClientSearchLoading(true);
        const cache = allClientesCacheRef.current;
        const coincidencias = cache.filter((c) =>
          (c.nombre || '').toLowerCase().includes(term)
        );
        coincidencias.sort((a, b) => {
          const nombreA = (a.nombre || '').toLowerCase();
          const nombreB = (b.nombre || '').toLowerCase();
          const aStartsWith = nombreA.startsWith(term);
          const bStartsWith = nombreB.startsWith(term);
          if (aStartsWith && !bStartsWith) return -1;
          if (!aStartsWith && bStartsWith) return 1;
          return nombreA.localeCompare(nombreB);
        });
        setClientSearchResults(coincidencias.slice(0, CLIENTS_SEARCH_LIMIT));
        setClientSearchLoading(false);
        lastClientSearchTermRef.current = term;
      }, CLIENTES_SEARCH_DEBOUNCE_MS);
      return () => clearTimeout(t);
    }
    if (term === '' && lastClientSearchTermRef.current !== '') {
      setClientSearchResults([]);
      lastClientSearchTermRef.current = '';
    }
  }, [clientSearchTerm]);

  // Lista a mostrar: con filtro = resultados de búsqueda (sobre todo el cache); sin filtro = primeros 50
  const displayedClients = clientSearchTerm.trim() ? clientSearchResults : clients;

  // Sincronizar "Seleccionar todos" con la lista mostrada
  useEffect(() => {
    if (displayedClients.length > 0) {
      setIsAllClientsSelected(
        selectedClients.length === displayedClients.length &&
        displayedClients.every(c => selectedClients.includes(c.id))
      );
    }
  }, [displayedClients, selectedClients]);

  // Nombre para mostrar de un cliente (chips): lista actual, cache completo o caché de seleccionados
  const getClientName = (clientId) => {
    const c = clients.find(x => x.id === clientId) || clientSearchResults.find(x => x.id === clientId);
    if (c?.nombre) return c.nombre;
    const fromCache = allClientesCacheRef.current.find(x => x.id === clientId);
    if (fromCache?.nombre) return fromCache.nombre;
    return selectedClientNames[clientId] ?? clientId;
  };

  useEffect(() => {
    if (!servicio?.id) return;
    getPdfDesigns(servicio.id).then(setPdfDesigns).catch(() => setPdfDesigns([]));
  }, [servicio?.id]);

  useEffect(() => {
    if (currentUser && existingFormId && servicio) {
      loadFormData(existingFormId);
    }
  }, [currentUser, existingFormId, servicio]);

  useEffect(() => {
    if (currentUser) {
      fetchServicio();
    }
  }, [currentUser]);

  // Cargar datos del formulario existente
  const loadFormData = async (formId) => {
    crudProfiler.startSession('LoadFormForUpdate', {
      entity: 'Form',
      formId,
      serviceId: servicio?.id
    });

    crudProfiler.startMeasurement('60_FetchFormData', {
      formId,
      serviceId: servicio?.id
    });

    try {
      if (!servicio) {
        console.error('No se ha cargado el servicio aún.');
        crudProfiler.endMeasurement('60_FetchFormData', { success: false, error: 'No service' });
        crudProfiler.endSession({ success: false, error: 'No service loaded' });
        return;
      }

      const formRef = doc(db, 'servicios', servicio.id, 'forms', formId);
      const docSnap = await getDoc(formRef);

      crudProfiler.endMeasurement('60_FetchFormData', {
        exists: docSnap.exists(),
        fieldsCount: docSnap.exists() ? Object.keys(docSnap.data().inputs || {}).length : 0
      });

      if (docSnap.exists()) {
        crudProfiler.startMeasurement('61_TransformFormData', {
          rawFieldsCount: Object.keys(docSnap.data().inputs || {}).length
        });

        const formData = docSnap.data();
        const entries = Object.entries(formData.inputs || {});
        const sortedEntries = entries.sort((a, b) => {
          const orderA = a[1].order != null ? a[1].order : 999;
          const orderB = b[1].order != null ? b[1].order : 999;
          return orderA - orderB;
        });
        const transformedInputs = sortedEntries.reduce((acc, [key, input]) => {
          acc[key] = { ...input, required: input.required !== false };
          return acc;
        }, {});

        crudProfiler.endMeasurement('61_TransformFormData', {
          transformedFieldsCount: Object.keys(transformedInputs).length
        });

        crudProfiler.startMeasurement('62_UpdateState');

        setForm({
          icon: formData?.icon,
          export: formData.export,
          name: formData.name,
          inputs: transformedInputs,
          cliente: formData.cliente || [],
          pdfDesignId: formData.pdfDesignId || ''
        });

        const clienteIds = formData.cliente || [];
        setSelectedClients(clienteIds);
        // Cargar nombres de clientes seleccionados (para chips si no están en los primeros 50)
        if (clienteIds.length > 0) {
          const names = {};
          await Promise.all(
            clienteIds.map(async (id) => {
              try {
                const ref = doc(db, 'servicios', servicio.id, 'clientes', id);
                const snap = await getDoc(ref);
                if (snap.exists()) names[id] = snap.data().nombre ?? id;
              } catch (_) {}
            })
          );
          setSelectedClientNames(names);
        }

        crudProfiler.endMeasurement('62_UpdateState', {
          fieldsSet: Object.keys(transformedInputs).length,
          clientsSet: (formData.cliente || []).length
        });

        crudProfiler.endSession({
          success: true,
          formLoaded: true,
          fieldsCount: Object.keys(transformedInputs).length
        });
      } else {
        crudProfiler.endSession({ success: false, error: 'Form not found' });
      }
    } catch (error) {
      console.error('Error al cargar los datos del formulario:', error);
      crudProfiler.endMeasurement('60_FetchFormData', { success: false, error: error.message });
      crudProfiler.endSession({ success: false, error: error.message });
    }
  };

  // Validación del formulario
  const isValid = () => {
    let validationErrors = {};
    if (!form.name.trim()) validationErrors.name = 'El nombre del formulario es obligatorio.';

    for (let input in form.inputs) {
      if (!form.inputs[input].title.trim()) validationErrors[`${input}_title`] = 'El nombre del campo es obligatorio.';
      if (!form.inputs[input].name.trim()) validationErrors[`${input}_name`] = 'El nombre es obligatorio.';
      if ((form.inputs[input].type === 'DROPBOX' || form.inputs[input].type === 'MULTI_DROPBOX') &&
        (!form.inputs[input].dropbox || form.inputs[input].dropbox.length === 0)) {
        validationErrors[`${input}_options`] = 'Debes agregar al menos una opción.';
      }
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  // Handlers de UI
  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);
  const handleOpenSuccessDialog = () => setOpenSuccessDialog(true);
  const handleInputClick = (id) => setSelectedInput(id);
  const handleSelectInputOption = (type) => setSelectedInputType(type);

  // Handler para seleccionar todos (los de la lista mostrada: 50 iniciales o resultados de búsqueda)
  const handleSelectAllClients = () => {
    const list = clientSearchTerm.trim() ? clientSearchResults : clients;
    const allIds = list.map(c => c.id);
    if (!isAllClientsSelected) {
      const names = { ...selectedClientNames };
      list.forEach(c => { names[c.id] = c.nombre; });
      setSelectedClientNames(names);
      setSelectedClients(allIds);
      setForm(prev => ({ ...prev, cliente: allIds }));
    } else {
      setSelectedClients([]);
      setForm(prev => ({ ...prev, cliente: [] }));
    }
    setIsAllClientsSelected(!isAllClientsSelected);
  };

  // Handler para drag and drop
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      crudProfiler.startMeasurement('66_ReorderFields', {
        fromIndex: Object.keys(form.inputs).indexOf(active.id),
        toIndex: Object.keys(form.inputs).indexOf(over.id),
        totalFields: Object.keys(form.inputs).length
      });

      const inputIds = Object.keys(form.inputs);
      const oldIndex = inputIds.indexOf(active.id);
      const newIndex = inputIds.indexOf(over.id);
      const newInputIds = arrayMove(inputIds, oldIndex, newIndex);

      const reorderedInputs = {};
      newInputIds.forEach((id, index) => {
        reorderedInputs[id] = { ...form.inputs[id], order: index };
      });

      setForm(prev => ({ ...prev, inputs: reorderedInputs }));

      crudProfiler.endMeasurement('66_ReorderFields', {
        success: true,
        newOrder: newInputIds.length
      });
    }
  };

  // Handler para cambios generales del formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'cliente') {
      setSelectedClient(value);
      if (value === 'all') {
        const allClientIds = clients.map(client => client.id);
        setForm(prev => ({ ...prev, [name]: allClientIds }));
      } else {
        setForm(prev => ({ ...prev, [name]: [value] }));
      }
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  // Handler para añadir un nuevo input
  const handleAddInput = () => {
    crudProfiler.startMeasurement('67_AddField', {
      fieldType: selectedInputType,
      currentFieldsCount: Object.keys(form.inputs || {}).length
    });

    const newInputId = uuidv4();
    const newOrder = Object.keys(form.inputs).length;
    setForm({
      ...form,
      inputs: {
        ...form.inputs,
        [newInputId]: {
          title: `Input ${Object.keys(form.inputs).length + 1}`,
          id: newInputId,
          name: '',
          order: newOrder,
          type: selectedInputType,
          required: true
        }
      }
    });
    setSelectedInputType(null);
    setIsDropdownOpen(false);

    crudProfiler.endMeasurement('67_AddField', {
      success: true,
      newFieldsCount: Object.keys(form.inputs).length + 1,
      fieldId: newInputId,
      fieldType: selectedInputType
    });
  };

  useEffect(() => {
    if (selectedInputType) {
      handleAddInput();
    }
  }, [selectedInputType]);

  // Submit del formulario
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    crudProfiler.startSession(existingFormId ? 'UpdateForm' : 'CreateFormFromUpdate', {
      entity: 'Form',
      formId: existingFormId || 'new',
      serviceId: servicio?.id,
      fieldsCount: Object.keys(form.inputs || {}).length,
      formName: form.name
    });

    crudProfiler.startMeasurement('63_ValidateForm', {
      fieldsCount: Object.keys(form.inputs || {}).length,
      hasName: !!form.name,
      hasIcon: !!form.icon
    });

    if (!isValid()) {
      crudProfiler.endMeasurement('63_ValidateForm', { valid: false, errors: Object.keys(errors).length });
      crudProfiler.endSession({ success: false, error: 'Validation failed' });
      handleOpenModal();
      return;
    }

    crudProfiler.endMeasurement('63_ValidateForm', { valid: true });

    if (!servicio) {
      console.error('No se encontró un servicio para el usuario actual.');
      crudProfiler.endSession({ success: false, error: 'No service found' });
      return;
    }

    crudProfiler.startMeasurement('64_PrepareFormData', {
      inputsCount: Object.keys(form.inputs).length
    });

    const inputsWithoutId = Object.keys(form.inputs).reduce((acc, key) => {
      const { id, ...rest } = form.inputs[key];
      acc[rest.title] = rest;
      return acc;
    }, {});

    crudProfiler.endMeasurement('64_PrepareFormData', {
      fieldsProcessed: Object.keys(inputsWithoutId).length
    });

    crudProfiler.startMeasurement(existingFormId ? '65_UpdateFormRecord' : '65_CreateFormRecord', {
      formSize: JSON.stringify(form).length,
      isUpdate: !!existingFormId
    });

    try {
      const formsRef = collection(db, 'servicios', servicio.id, 'forms');
      if (existingFormId) {
        const formDocRef = doc(db, 'servicios', servicio.id, 'forms', existingFormId);
        await updateDoc(formDocRef, { ...form, inputs: inputsWithoutId, name: form.name, pdfDesignId: form.pdfDesignId || null });

        crudProfiler.endMeasurement('65_UpdateFormRecord', {
          success: true,
          formId: existingFormId,
          fieldsUpdated: Object.keys(inputsWithoutId).length
        });

        console.log('Formulario actualizado correctamente:', form);
        crudProfiler.endSession({
          success: true,
          formId: existingFormId,
          fieldsCount: Object.keys(inputsWithoutId).length,
          operation: 'update'
        });

        handleOpenSuccessDialog();
      } else {
        const docRef = await addDoc(formsRef, { ...form, inputs: inputsWithoutId, name: form.name, pdfDesignId: form.pdfDesignId || null });

        crudProfiler.endMeasurement('65_CreateFormRecord', {
          success: true,
          formId: docRef.id,
          fieldsCreated: Object.keys(inputsWithoutId).length
        });

        console.log('Formulario creado correctamente:', form);
        crudProfiler.endSession({
          success: true,
          formId: docRef.id,
          fieldsCount: Object.keys(inputsWithoutId).length,
          operation: 'create'
        });
      }
    } catch (error) {
      console.error('Error al enviar el formulario:', error);
      crudProfiler.endMeasurement(existingFormId ? '65_UpdateFormRecord' : '65_CreateFormRecord', {
        success: false,
        error: error.message
      });
      crudProfiler.endSession({ success: false, error: error.message });
    }
  };

  // Filtrar inputs para preview
  const getFilteredInputs = (inputs) => {
    return Object.fromEntries(
      Object.entries(inputs).map(([title, input]) => {
        const { order, ...filteredInput } = input;
        return [title, filteredInput];
      })
    );
  };

  return (
    <div className='formCreatorContainer'>
      <Button
        variant="outlined"
        color="primary"
        onClick={() => navigate(-1)}
        style={{ marginLeft: '20px' }}
      >
        Volver
      </Button>
      <div className='sections'>
        <div className="left-section">
          <form onSubmit={handleFormSubmit}>
            <IconPicker
              selectedIcon={form.icon}
              onSelect={(iconURL) => setForm(prev => ({ ...prev, icon: iconURL }))}
            />
            <FormControl sx={{ width: 100, marginRight: 2, marginTop: 1 }}>
              <InputLabel htmlFor="export">Export</InputLabel>
              <Select
                labelId="export-label"
                id="export"
                name="export"
                value={form.export}
                onChange={handleInputChange}
              >
                <MenuItem value="">
                  <em>Seleccione...</em>
                </MenuItem>
                <MenuItem value="csv">CSV</MenuItem>
                <MenuItem value="pdf">PDF</MenuItem>
                <MenuItem value="foto">Fotografía</MenuItem>
              </Select>
            </FormControl>
            {errors.export && <Typography color="error">{errors.export}</Typography>}

            {(form.export === 'pdf' || (Array.isArray(form.export) && form.export.includes('pdf'))) && (
              <FormControl sx={{ width: 200, marginRight: 2, marginTop: 1 }}>
                <InputLabel id="pdf-design-label" shrink>Diseño PDF</InputLabel>
                <Select
                  labelId="pdf-design-label"
                  id="pdf-design"
                  name="pdfDesignId"
                  value={form.pdfDesignId || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, pdfDesignId: e.target.value }))}
                  displayEmpty
                  label="Diseño PDF"
                  notched
                >
                  <MenuItem value="">Sin diseño (por defecto)</MenuItem>
                  {pdfDesigns.map((d) => (
                    <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl sx={{ width: 320, marginRight: 2, marginTop: 1 }}>
              <InputLabel htmlFor="client-selection">Cliente</InputLabel>
              <Select
                value={selectedClients}
                multiple
                onClose={() => setClientSearchTerm('')}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.includes('all')) {
                    handleSelectAllClients();
                  } else {
                    setSelectedClients(value);
                    setForm(prev => ({ ...prev, cliente: value }));
                  }
                }}
                input={<Input id="select-multiple-chip" />}
                renderValue={(selected) => (
                  <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                    {selected.includes('all') ? (
                      <Chip label="Todos los clientes" style={{ marginRight: 2, marginLeft: 2 }} />
                    ) : (
                      selected.map((clientId) => (
                        <Chip
                          key={clientId}
                          label={getClientName(clientId)}
                          style={{ marginRight: 2, marginLeft: 2 }}
                        />
                      ))
                    )}
                  </div>
                )}
              >
                <MenuItem sx={{ py: 0.5 }} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Buscar cliente..."
                    value={clientSearchTerm}
                    onChange={(e) => setClientSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    inputProps={{ 'aria-label': 'Buscar cliente' }}
                  />
                </MenuItem>
                <MenuItem value="all" onClick={handleSelectAllClients}>
                  <Checkbox checked={isAllClientsSelected} />
                  <ListItemText primary="Seleccionar todos los clientes" />
                </MenuItem>
                {clientSearchLoading ? (
                  <MenuItem disabled>
                    <ListItemText primary="Buscando..." />
                  </MenuItem>
                ) : displayedClients.length === 0 ? (
                  <MenuItem disabled>
                    <ListItemText primary={clientSearchTerm.trim() ? 'Sin coincidencias' : 'No hay clientes'} />
                  </MenuItem>
                ) : (
                  displayedClients.map(client => (
                    <MenuItem
                      key={client.id}
                      value={client.id}
                      onClick={() => {
                        setSelectedClientNames(prev => ({ ...prev, [client.id]: client.nombre }));
                      }}
                    >
                      <Checkbox checked={selectedClients.includes(client.id)} />
                      <ListItemText primary={client.nombre} />
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            <div className='form-name-row'>
              <label htmlFor="name" className='form-name-label'>Nombre del Formulario</label>
              <TextField
                id="name"
                name="name"
                value={form.name || ''}
                onChange={handleInputChange}
                placeholder="Ej: Parte de trabajo, Denuncia..."
                className='form-name-input'
                variant="outlined"
                fullWidth
              />
              {errors.name && <Typography color="error">{errors.name}</Typography>}
            </div>

            <div style={{ position: 'relative', marginBottom: 2 }}>
              <Button
                type="button"
                onClick={(event) => {
                  setIsDropdownOpen(!isDropdownOpen);
                  setAnchorEl(event.currentTarget);
                }}
                className="action-button"
                sx={{ marginBottom: 2 }}
              >
                Añadir Input
              </Button>

              {/* Menú de tipos de input */}
              <InputTypeMenu
                isOpen={isDropdownOpen}
                anchorEl={anchorEl}
                onClose={() => {
                  setIsDropdownOpen(false);
                  setAnchorEl(null);
                }}
                onSelectType={handleSelectInputOption}
              />
            </div>

            <Button type="submit" className="action-button" sx={{ marginTop: 1 }}>
              Actualizar Formulario
            </Button>

            {/* Lista de inputs arrastrables */}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={Object.keys(form.inputs)}
                strategy={verticalListSortingStrategy}
              >
                {Object.entries(form.inputs).map(([id, input], index) => (
                  <SortableInput
                    key={id}
                    id={id}
                    input={input}
                    index={index}
                    selectedInput={selectedInput}
                    handleInputClick={handleInputClick}
                    handleTitleChange={handlers.handleTitleChange}
                    handleInputUpdate={handlers.handleInputUpdate}
                    handleRequiredChange={handlers.handleRequiredChange}
                    handleSizeChange={handlers.handleSizeChange}
                    handleAddOption={handlers.handleAddOption}
                    handleRemoveOption={handlers.handleRemoveOption}
                    handleOptionChange={handlers.handleOptionChange}
                    handleRemoveInput={handlers.handleRemoveInput}
                    handleAddConditionalField={handlers.handleAddConditionalField}
                    handleRemoveConditionalField={handlers.handleRemoveConditionalField}
                    handleConditionalFieldChange={handlers.handleConditionalFieldChange}
                    handleAddConditionalDropboxOption={handlers.handleAddConditionalDropboxOption}
                    handleRemoveConditionalDropboxOption={handlers.handleRemoveConditionalDropboxOption}
                    handleConditionalDropboxOptionChange={handlers.handleConditionalDropboxOptionChange}
                    handleAddNestedConditionalField={handlers.handleAddNestedConditionalField}
                    handleRemoveNestedConditionalField={handlers.handleRemoveNestedConditionalField}
                    handleNestedConditionalFieldChange={handlers.handleNestedConditionalFieldChange}
                    handleAddNestedConditionalDropboxOption={handlers.handleAddNestedConditionalDropboxOption}
                    handleRemoveNestedConditionalDropboxOption={handlers.handleRemoveNestedConditionalDropboxOption}
                    handleNestedConditionalDropboxOptionChange={handlers.handleNestedConditionalDropboxOptionChange}
                    handleAddSectionField={handlers.handleAddSectionField}
                    handleRemoveSectionField={handlers.handleRemoveSectionField}
                    handleSectionFieldChange={handlers.handleSectionFieldChange}
                    handleAddSectionFieldDropboxOption={handlers.handleAddSectionFieldDropboxOption}
                    handleRemoveSectionFieldDropboxOption={handlers.handleRemoveSectionFieldDropboxOption}
                    handleSectionFieldDropboxOptionChange={handlers.handleSectionFieldDropboxOptionChange}
                    handleAddSectionConditionalField={handlers.handleAddSectionConditionalField}
                    handleRemoveSectionConditionalField={handlers.handleRemoveSectionConditionalField}
                    handleSectionConditionalFieldChange={handlers.handleSectionConditionalFieldChange}
                    handleAddSectionConditionalDropboxOption={handlers.handleAddSectionConditionalDropboxOption}
                    handleRemoveSectionConditionalDropboxOption={handlers.handleRemoveSectionConditionalDropboxOption}
                    handleSectionConditionalDropboxOptionChange={handlers.handleSectionConditionalDropboxOptionChange}
                    handleConditionalRepeatableSectionFieldAdd={handlers.handleConditionalRepeatableSectionFieldAdd}
                    handleConditionalRepeatableSectionFieldRemove={handlers.handleConditionalRepeatableSectionFieldRemove}
                    handleConditionalRepeatableSectionFieldChange={handlers.handleConditionalRepeatableSectionFieldChange}
                    handleConditionalRepeatableSectionDropboxOptionAdd={handlers.handleConditionalRepeatableSectionDropboxOptionAdd}
                    handleConditionalRepeatableSectionDropboxOptionRemove={handlers.handleConditionalRepeatableSectionDropboxOptionRemove}
                    handleConditionalRepeatableSectionDropboxOptionChange={handlers.handleConditionalRepeatableSectionDropboxOptionChange}
                    handleConditionalRepeatableSectionUpdate={handlers.handleConditionalRepeatableSectionUpdate}
                    errors={errors}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </form>
        </div>
        <div className="right-section">
          <FormPreview formTitle={form.name} formInputs={getFilteredInputs(form.inputs)} onInputClick={handleInputClick} />
        </div>
      </div>

      {/* Dialog de éxito */}
      <Dialog
        open={openSuccessDialog}
        onClose={() => setOpenSuccessDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Formulario actualizado"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            El formulario se ha actualizado exitosamente.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSuccessDialog(false)} color="primary">
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FormUpdate;
